import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, ChevronDown, CreditCard, IndianRupee, MapPin, Package, Phone, Send, X } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import Button from '../common/Button.js';
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert.js';
import PaymentQrModal from '../common/PaymentQrModal.js';
import { useAuth } from '../../context/AuthContext.js';
import { locationHubService } from '../../services/locationHubService.js';
import { shipmentService } from '../../services/shipmentService.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import companySettingsService from '../../services/companySettingsService.js';
import paidAmountSettlementService from '../../services/paidAmountSettlementService.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import { toLocationsForBooking } from '../../utils/destinationHubMapping.js';
import { hasAutoMlAccess, hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import {
  calcInsuranceFromSettings,
  resolveCargoDefaults,
  isShipmentChargeSplitMode,
  allocateShipmentChargeSplit,
} from '../../utils/cargoParcelUtils.js';
import { DEFAULT_LABOUR_CHARGE_POLICY, labourCapturedAt, normalizeLabourChargePolicy } from '../../utils/labourChargePolicy.js';
import ShipmentMediaUploader from './ShipmentMediaUploader.js';
import { scheduleBookingPrintJobs } from '../../utils/printShipmentReceipt.js';

const ALL_PAYMENT_MODES = [
  { value: 'to_pay', label: 'To Pay', flag: 'allow_to_pay' },
  { value: 'paid', label: 'Paid', flag: 'allow_paid' },
  { value: 'credit', label: 'Credit', flag: 'allow_on_account' },
];

const hubMatchesId = (hub, rawId) => {
  if (rawId == null || rawId === '') return false;
  const wanted = String(rawId);
  return [hub.id, hub.hub_id].some((value) => value != null && String(value) === wanted);
};

const hubLabel = (hub) => formatLocationDropdownLabel(hub) || hub?.hub_id || '';

const paymentOptionsForHub = (hub, restrict) => {
  if (!restrict || !hub) return ALL_PAYMENT_MODES;
  return ALL_PAYMENT_MODES.filter((mode) => hub[mode.flag] !== false);
};

const roundRupee = (value) => Math.round(Number(value) || 0);
const ceilRupee = (value) => {
  const paisa = Math.round((Number(value) || 0) * 100) / 100;
  return Math.ceil(paisa - 1e-9);
};

const toCharge = (defaults, key) => {
  const cfg = defaults?.[key];
  if (!cfg?.enabled) return 0;
  return Number(cfg.amount) || 0;
};

const locLabel = (loc) => {
  const name = loc?.location || loc?.hub_name || loc?.name || loc?.display_label || '';
  const code = loc?.location_code || loc?.hub_code;
  if (!name) return code || '';
  return code ? `${name} (${code})` : name;
};

const locationsLine = (locations) =>
  (locations || []).map(locLabel).filter(Boolean).join(', ');

const sheetDetails = (sheet) => {
  const from = locationsLine(sheet.from_locations) || 'From';
  const to = locationsLine(sheet.to_locations) || 'To';
  const crossing = locationsLine(sheet.crossing_locations);
  const vehicle = sheet.vehicle_number || 'Vehicle';
  const count = Array.isArray(sheet.shipment_ids) ? sheet.shipment_ids.length : 0;
  const amount = Number(sheet.total_amount);
  const amountLabel = Number.isFinite(amount) && amount > 0 ? ` · ₹${amount.toFixed(0)}` : '';
  const routeName = sheet.route?.route_name || sheet.route?.name || '';
  const extras = [
    sheet.driver_contact ? `Driver ${sheet.driver_contact}` : '',
    sheet.created_by_name ? `By ${sheet.created_by_name}` : '',
  ].filter(Boolean);
  return {
    vehicle,
    route: `${from} → ${to}`,
    crossing: crossing ? `Via ${crossing}` : '',
    routeName,
    mlId: sheet.ml_sheet_id || '',
    meta: `${sheet.status || 'OPEN'} · ${count} shipment${count === 1 ? '' : 's'}${amountLabel}`,
    extras: extras.join(' · '),
    searchText: [
      vehicle,
      from,
      to,
      crossing,
      routeName,
      sheet.ml_sheet_id,
      sheet.driver_contact,
      sheet.created_by_name,
      sheet.status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
};

const SheetOptionBody = ({ details, selected, compact = false }) => {
  if (compact) {
    return (
      <>
        <span className={`block text-sm font-semibold break-words ${selected ? 'text-blue-700' : 'text-gray-800'}`}>
          {details.vehicle}
          {details.mlId ? ` · ${details.mlId}` : ''}
        </span>
        <span className="block text-xs text-gray-600 break-words whitespace-normal">
          {[details.route, details.crossing].filter(Boolean).join(' ')}
        </span>
      </>
    );
  }
  return (
    <>
      <span className={`block text-sm font-semibold break-words ${selected ? 'text-blue-700' : 'text-gray-800'}`}>
        {details.vehicle}
      </span>
      <span className="block text-xs text-gray-600 break-words whitespace-normal">{details.route}</span>
      {details.routeName ? (
        <span className="block text-xs text-gray-600 break-words whitespace-normal">{details.routeName}</span>
      ) : null}
      {details.crossing ? (
        <span className="block text-xs text-gray-600 break-words whitespace-normal">{details.crossing}</span>
      ) : null}
      <span className="block text-xs text-gray-500 break-words">
        {details.mlId} · {details.meta}
      </span>
      {details.extras ? (
        <span className="block text-xs text-gray-500 break-words whitespace-normal">{details.extras}</span>
      ) : null}
    </>
  );
};

const sheetServesReceiver = (sheet, receiverHub) => {
  if (!receiverHub) return false;
  const wanted = new Set(
    [receiverHub.id, receiverHub.parent_hub_id]
      .filter((value) => value != null && value !== '')
      .map(String),
  );
  const toIds = (sheet.to_location_ids || []).map(String);
  if (toIds.some((id) => wanted.has(id))) return true;
  const receiverName = String(receiverHub.location || '').trim().toLowerCase();
  if (!receiverName) return false;
  return (sheet.to_locations || []).some((loc) => {
    const name = String(loc.location || loc.hub_name || loc.name || '').trim().toLowerCase();
    return name === receiverName;
  });
};

const useIsMobileLayout = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (event) => setIsMobile(event.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
};

const MobilePickerSheet = ({ title, search, onSearch, searchPlaceholder, children, onClose }) => {
  const [maxHeight, setMaxHeight] = useState('92dvh');

  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => {
      const height = viewport?.height || window.innerHeight;
      setMaxHeight(`${Math.round(height * 0.92)}px`);
    };
    update();
    viewport?.addEventListener('resize', update);
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      viewport?.removeEventListener('resize', update);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex flex-col justify-end bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full bg-white rounded-t-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ maxHeight }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-800 truncate">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-3 py-2 border-b border-gray-100">
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400"
            enterKeyHint="search"
          />
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 pb-[env(safe-area-inset-bottom)]">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const PickupLocationSelect = ({
  name,
  value,
  hubs,
  placeholder,
  onChange,
  excludeId,
  variant = 'inline',
  title,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const isMobile = useIsMobileLayout();
  const selected = hubs.find((hub) => String(hub.id) === String(value));
  const filtered = sortLocationsAsc(hubs.filter((hub) => {
    if (excludeId && String(hub.id) === String(excludeId)) return false;
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return hubLabel(hub).toLowerCase().includes(term);
  }));
  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('touchstart', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('touchstart', onClickOutside);
    };
  }, [open, isMobile]);

  const isForm = variant === 'form';
  const sheetTitle = title || placeholder || 'Select location';
  const optionsList = filtered.length === 0 ? (
    <p className="px-3 py-3 text-sm text-gray-400">No locations found</p>
  ) : (
    filtered.map((hub) => (
      <button
        key={`${name}-${hub.id}`}
        type="button"
        className={`w-full text-left px-3 py-3 text-sm hover:bg-blue-50 whitespace-normal break-words ${
          String(hub.id) === String(value) ? 'bg-blue-50 text-blue-700' : 'text-gray-800'
        }`}
        onClick={() => {
          onChange({ target: { name, value: String(hub.id) } });
          close();
        }}
      >
        {hubLabel(hub)}
      </button>
    ))
  );

  return (
    <div ref={containerRef} className={`relative min-w-0 ${isForm ? 'w-full' : 'flex-1'} ${open && !isMobile ? 'z-[80]' : ''}`}>
      <button
        type="button"
        name={name}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
        className={
          isForm
            ? `w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm border rounded-xl bg-white ${
                open ? 'border-blue-400 ring-2 ring-blue-400' : 'border-gray-300'
              }`
            : 'w-full flex items-center justify-between gap-2 py-2 text-left text-sm bg-transparent'
        }
      >
        <span className={`min-w-0 ${isForm ? 'flex-1 whitespace-normal break-words' : 'truncate'} ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
          {selected ? hubLabel(selected) : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && isMobile && (
        <MobilePickerSheet
          title={sheetTitle}
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Search location..."
          onClose={close}
        >
          {optionsList}
        </MobilePickerSheet>
      )}
      {open && !isMobile && (
        <div className="absolute z-[200] left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search location..."
            className="w-full px-3 py-2 text-sm border-b border-gray-100 outline-none"
          />
          <div className="max-h-52 overflow-y-auto">
            {optionsList}
          </div>
        </div>
      )}
    </div>
  );
};

const PickupMlSheetSelect = ({
  value,
  sheets,
  disabled,
  placeholder,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const isMobile = useIsMobileLayout();
  const selected = sheets.find((sheet) => sheet.ml_sheet_id === value);
  const selectedDetails = selected ? sheetDetails(selected) : null;
  const filtered = sheets.filter((sheet) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return sheetDetails(sheet).searchText.includes(term);
  });
  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('touchstart', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('touchstart', onClickOutside);
    };
  }, [open, isMobile, close]);

  useEffect(() => {
    if (disabled) {
      close();
    }
  }, [disabled, close]);

  const optionsList = (
    <>
      <button
        type="button"
        className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 whitespace-normal break-words ${
          !value ? 'bg-blue-50 text-blue-700' : 'text-gray-800'
        }`}
        onClick={() => {
          onChange({ target: { name: 'existing_ml_sheet_id', value: '' } });
          close();
        }}
      >
        Book only (no ML sheet)
      </button>
      {filtered.length === 0 ? (
        search.trim() ? (
          <p className="px-3 py-3 text-sm text-gray-400">No matching ML sheet</p>
        ) : (
          <p className="px-3 py-3 text-sm text-gray-400">No ML sheets for this destination today</p>
        )
      ) : (
        filtered.map((sheet) => {
          const details = sheetDetails(sheet);
          const isSelected = sheet.ml_sheet_id === value;
          return (
            <button
              key={sheet.ml_sheet_id}
              type="button"
              className={`w-full text-left px-3 py-2.5 hover:bg-blue-50 whitespace-normal break-words border-t border-gray-100 ${
                isSelected ? 'bg-blue-50' : ''
              }`}
              onClick={() => {
                onChange({ target: { name: 'existing_ml_sheet_id', value: sheet.ml_sheet_id } });
                close();
              }}
            >
              <SheetOptionBody details={details} selected={isSelected} />
            </button>
          );
        })
      )}
    </>
  );

  return (
    <div ref={containerRef} className={`relative min-w-0 ${open && !isMobile ? 'z-[80]' : ''}`}>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`w-full flex items-start justify-between gap-2 px-3 py-2.5 text-left text-sm border rounded-xl bg-white ${
          open ? 'border-blue-400 ring-2 ring-blue-400' : 'border-gray-300'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span className="min-w-0 flex-1 whitespace-normal break-words">
          {selectedDetails ? (
            <SheetOptionBody details={selectedDetails} selected={false} compact />
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 mt-0.5 text-gray-400 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && !disabled && isMobile && (
        <MobilePickerSheet
          title="Select vehicle (today's ML)"
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Search vehicle, route, or ML..."
          onClose={close}
        >
          {optionsList}
        </MobilePickerSheet>
      )}
      {open && !disabled && !isMobile && (
        <div className="absolute z-[200] left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vehicle, route, or ML..."
            className="w-full px-3 py-2 text-sm border-b border-gray-100 outline-none"
          />
          <div className="max-h-72 overflow-y-auto overscroll-contain">
            {optionsList}
          </div>
        </div>
      )}
    </div>
  );
};

const PickupManAddShipment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mlSheets, setMlSheets] = useState([]);
  const [mlCreationBlock, setMlCreationBlock] = useState({ blocked: false });
  const [mlBlockLoaded, setMlBlockLoaded] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [chargeDefaults, setChargeDefaults] = useState(resolveCargoDefaults());
  const [labourPolicy, setLabourPolicy] = useState(DEFAULT_LABOUR_CHARGE_POLICY);
  const [companyRcm, setCompanyRcm] = useState({ enabled: false, igst: 0, cgst: 0, sgst: 0 });
  const [showPaymentQr, setShowPaymentQr] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [formData, setFormData] = useState({
    sender_location_hub_id: user?.location_hub_id ? String(user.location_hub_id) : '',
    receiver_location_hub_id: '',
    sender_contact: '',
    receiver_contact: '',
    no_of_articles: '',
    amount: '',
    mode_of_payment: 'to_pay',
    payment_mode: '',
    paid_amount: '',
    existing_ml_sheet_id: '',
  });
  const isMobileLayout = useIsMobileLayout();

  const gatewayEnabled = Boolean(
    user?.company_features?.payment_gateway || user?.company_can_access?.payment_gateway,
  );
  const personalUpiQrEnabled = Boolean(user?.personal_upi_qr_enabled);
  const branchUpiQrEnabled = Boolean(
    user?.branch_upi_qr_enabled && String(user?.branch_upi_id || '').includes('@'),
  );
  const upiQrWillOpen = branchUpiQrEnabled || gatewayEnabled || personalUpiQrEnabled;

  useEffect(() => {
    const loadHubs = async () => {
      try {
        const response = await locationHubService.getHubsForSelect();
        const list = response.data?.hubs || response.data?.data || [];
        setHubs(Array.isArray(list) ? list : []);
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to load locations');
      }
    };
    loadHubs();
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await companySettingsService.getSettings();
        const payload = res?.data || {};
        if (payload.rcm) {
          setCompanyRcm({ enabled: false, igst: 0, cgst: 0, sgst: 0, ...payload.rcm });
        }
        const insurance = payload.shipment_charge_defaults?.insurance
          || payload.shipment_insurance_charge
          || {};
        const defaults = resolveCargoDefaults(
          payload.shipment_charge_defaults || payload.cargo_charge_defaults,
        );
        defaults.insurance = {
          enabled: !!insurance.enabled,
          amount: Number.parseFloat(insurance.amount) || 0,
          mode: insurance.mode === 'percent' ? 'percent' : 'fixed',
        };
        setChargeDefaults(defaults);
        setLabourPolicy(normalizeLabourChargePolicy(payload.labour_charge_policy));
      } catch (_) {
        // Keep zero extras if settings are unavailable
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (!hubs.length) return;
    setFormData((prev) => {
      const assigned = hubs.find((hub) => hubMatchesId(hub, user?.location_hub_id));
      const current = hubs.find((hub) => hubMatchesId(hub, prev.sender_location_hub_id));
      if (current) {
        return String(current.id) === String(prev.sender_location_hub_id)
          ? prev
          : { ...prev, sender_location_hub_id: String(current.id) };
      }
      if (assigned) {
        return { ...prev, sender_location_hub_id: String(assigned.id) };
      }
      return prev;
    });
  }, [hubs, user?.location_hub_id]);

  useEffect(() => {
    if (!hasAutoMlAccess(user)) {
      setMlSheets([]);
      setMlCreationBlock({ blocked: false });
      setMlBlockLoaded(true);
      return undefined;
    }
    let cancelled = false;
    const today = (() => {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })();
    setMlBlockLoaded(false);
    paidAmountSettlementService
      .getMlCreationBlock()
      .then((res) => {
        if (!cancelled) setMlCreationBlock(res?.data || { blocked: false });
      })
      .catch(() => {
        if (!cancelled) setMlCreationBlock({ blocked: false });
      })
      .finally(() => {
        if (!cancelled) setMlBlockLoaded(true);
      });
    mlSheetService
      .getAllMLSheets(1, 200, { from_date: today, to_date: today, status: 'OPEN', all: 'true' })
      .then((res) => {
        if (cancelled) return;
        const sheets = res.data?.data || res.data?.ml_sheets || [];
        const openSheets = (Array.isArray(sheets) ? sheets : []).filter(
          (sheet) => (
            String(sheet?.status || '').toUpperCase() !== 'CLOSE'
            && !(sheet.edit_blockers || []).length
          ),
        );
        setMlSheets(openSheets);
      })
      .catch(() => {
        if (!cancelled) setMlSheets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const restrictHubs = hasInternalHubDispatchControls(user);
  const senderHub = useMemo(
    () => hubs.find((hub) => String(hub.id) === String(formData.sender_location_hub_id)),
    [hubs, formData.sender_location_hub_id],
  );
  const receiverHub = useMemo(
    () => hubs.find((hub) => String(hub.id) === String(formData.receiver_location_hub_id)),
    [hubs, formData.receiver_location_hub_id],
  );
  const paymentModes = useMemo(
    () => paymentOptionsForHub(senderHub, restrictHubs),
    [senderHub, restrictHubs],
  );
  const pickupHubs = hubs;
  const destinationHubs = useMemo(
    () => toLocationsForBooking(hubs, formData.sender_location_hub_id),
    [hubs, formData.sender_location_hub_id],
  );
  const receiverMlSheets = useMemo(
    () => mlSheets.filter((sheet) => sheetServesReceiver(sheet, receiverHub)),
    [mlSheets, receiverHub],
  );

  useEffect(() => {
    if (!formData.existing_ml_sheet_id) return;
    const stillValid = receiverMlSheets.some(
      (sheet) => sheet.ml_sheet_id === formData.existing_ml_sheet_id,
    );
    if (!stillValid) {
      setFormData((prev) => ({ ...prev, existing_ml_sheet_id: '' }));
    }
  }, [receiverMlSheets, formData.existing_ml_sheet_id]);

  const extras = useMemo(() => {
    const splitMode = isShipmentChargeSplitMode(chargeDefaults, labourPolicy);
    const entered = Number.parseFloat(formData.amount) || 0;
    const allocated = splitMode
      ? allocateShipmentChargeSplit(chargeDefaults, entered, {}, labourPolicy)
      : null;
    const freight = splitMode ? allocated.freight : entered;
    const insurance = calcInsuranceFromSettings(chargeDefaults.insurance, freight);
    return {
      freight,
      docket: splitMode ? allocated.extras.docket : toCharge(chargeDefaults, 'docket'),
      handling: splitMode ? allocated.extras.handling : toCharge(chargeDefaults, 'handling'),
      doorPickup: splitMode ? allocated.extras.door_pickup : toCharge(chargeDefaults, 'door_pickup'),
      doorDelivery: splitMode ? allocated.extras.door_delivery : toCharge(chargeDefaults, 'door_delivery'),
      loading: labourCapturedAt(labourPolicy, 'loading', 'booking')
        ? (splitMode ? allocated.extras.loading : toCharge(chargeDefaults, 'loading'))
        : 0,
      unloading: labourCapturedAt(labourPolicy, 'unloading', 'booking')
        ? (splitMode ? (allocated.extras.unloading || 0) : toCharge(chargeDefaults, 'unloading'))
        : 0,
      toll: splitMode ? allocated.extras.toll : toCharge(chargeDefaults, 'toll'),
      fuel: splitMode ? allocated.extras.fuel_surcharge : toCharge(chargeDefaults, 'fuel_surcharge'),
      packing: splitMode ? allocated.extras.packing : toCharge(chargeDefaults, 'packing'),
      insurance,
      splitMode,
    };
  }, [formData.amount, chargeDefaults, labourPolicy]);

  const gstRates = useMemo(() => {
    if (!companyRcm.enabled) {
      return { sgst: 0, cgst: 0, igst: 0 };
    }
    const senderState = (senderHub?.state || '').trim().toLowerCase();
    const receiverState = (receiverHub?.state || '').trim().toLowerCase();
    const sameState = !!(senderState && receiverState && senderState === receiverState);
    if (sameState) {
      let sgst = Number.parseFloat(companyRcm.sgst) || 0;
      let cgst = Number.parseFloat(companyRcm.cgst) || 0;
      if (sgst === 0 && cgst === 0) {
        sgst = 2.5;
        cgst = 2.5;
      }
      return { sgst, cgst, igst: 0 };
    }
    let igst = Number.parseFloat(companyRcm.igst) || 0;
    if (igst === 0) igst = 5;
    return { sgst: 0, cgst: 0, igst };
  }, [companyRcm, senderHub, receiverHub]);

  const totals = useMemo(() => {
    const subtotal = Math.max(
      0,
      extras.freight
        + extras.docket
        + extras.handling
        + extras.doorPickup
        + extras.doorDelivery
        + extras.loading
        + extras.unloading
        + extras.toll
        + extras.fuel
        + extras.packing
        + extras.insurance,
    );
    const gstBase = Math.max(0, extras.freight);
    const sgst = roundRupee((gstBase * gstRates.sgst) / 100);
    const cgst = roundRupee((gstBase * gstRates.cgst) / 100);
    const igst = roundRupee((gstBase * gstRates.igst) / 100);
    const gst = sgst + cgst + igst;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      sgst,
      cgst,
      igst,
      gst,
      total: ceilRupee(subtotal + gst),
    };
  }, [extras, gstRates]);

  useEffect(() => {
    if (!paymentModes.length) return;
    const currentOk = paymentModes.some((mode) => mode.value === formData.mode_of_payment);
    if (currentOk) return;
    const fallback = paymentModes.find((mode) => mode.value === 'to_pay') || paymentModes[0];
    setFormData((prev) => ({
      ...prev,
      mode_of_payment: fallback.value,
      payment_mode: fallback.value === 'paid' ? (prev.payment_mode || 'cash') : '',
      paid_amount: fallback.value === 'paid' ? String(totals.total || 0) : '',
    }));
  }, [paymentModes, formData.mode_of_payment, totals.total]);

  useEffect(() => {
    if (!formData.receiver_location_hub_id) return;
    const stillValid = destinationHubs.some(
      (hub) => String(hub.id) === String(formData.receiver_location_hub_id),
    );
    if (!stillValid) {
      setFormData((prev) => ({ ...prev, receiver_location_hub_id: '', existing_ml_sheet_id: '' }));
    }
  }, [destinationHubs, formData.receiver_location_hub_id]);

  useEffect(() => {
    if (formData.mode_of_payment !== 'paid') return;
    const nextPaid = String(totals.total || 0);
    setFormData((prev) => (prev.paid_amount === nextPaid ? prev : { ...prev, paid_amount: nextPaid }));
  }, [
    formData.mode_of_payment,
    totals.total,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === 'sender_contact' || name === 'receiver_contact') {
      next = String(value || '').replace(/\D/g, '').slice(0, 10);
    }
    if (name === 'no_of_articles') {
      next = String(value || '').replace(/\D/g, '').slice(0, 4);
    }
    if (name === 'amount' || name === 'paid_amount') {
      next = String(value || '').replace(/[^0-9.]/g, '');
    }
    if (name === 'paid_amount' && next !== '') {
      const paid = Number.parseFloat(next) || 0;
      if (paid > totals.total) {
        next = String(totals.total);
      }
    }
    setFormData((prev) => {
      const updated = { ...prev, [name]: next };
      if (name === 'sender_location_hub_id') {
        updated.existing_ml_sheet_id = '';
        if (String(updated.receiver_location_hub_id) === String(next)) {
          updated.receiver_location_hub_id = '';
        }
      }
      if (name === 'receiver_location_hub_id' && String(updated.sender_location_hub_id) === String(next)) {
        updated.sender_location_hub_id = '';
        updated.existing_ml_sheet_id = '';
      }
      if (name === 'mode_of_payment') {
        if (next === 'paid') {
          updated.payment_mode = prev.payment_mode || 'cash';
          updated.paid_amount = String(totals.total || 0);
        } else {
          updated.payment_mode = '';
          updated.paid_amount = '';
        }
      }
      return updated;
    });
  };

  const finishBookingSuccess = async (response, intentResult) => {
    const created = intentResult?.shipment || response?.data?.shipment || response?.data?.data || {};
    const shipmentId = created.shipment_id || response?.data?.shipment_id;
    const mlSheetId = intentResult?.ml_sheet?.ml_sheet_id || response?.data?.ml_sheet?.ml_sheet_id;
    Alert.success(
      mlSheetId
        ? `Shipment ${shipmentId} booked and added to ML ${mlSheetId}`
        : `Shipment ${shipmentId} booked`,
    );
    if (shipmentId) {
      navigate('/shipments');
      scheduleBookingPrintJobs(shipmentId, user);
    } else {
      navigate('/shipments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sender_location_hub_id) {
      Alert.error('Select pickup location');
      return;
    }
    if (!formData.receiver_location_hub_id) {
      Alert.error('Select destination');
      return;
    }
    if (String(formData.sender_contact).length !== 10) {
      Alert.error('Enter a valid consigner mobile number');
      return;
    }
    if (String(formData.receiver_contact).length !== 10) {
      Alert.error('Enter a valid consignee mobile number');
      return;
    }
    if (!formData.no_of_articles || Number(formData.no_of_articles) < 1) {
      Alert.error('Enter number of parcels');
      return;
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      Alert.error(extras.splitMode ? 'Enter freight (article rate)' : 'Enter amount');
      return;
    }
    if (!formData.mode_of_payment) {
      Alert.error('Select payment mode');
      return;
    }
    if (formData.mode_of_payment === 'paid' && !formData.payment_mode) {
      Alert.error('Select cash or UPI');
      return;
    }

    const paidAmount = formData.mode_of_payment === 'paid'
      ? Math.min(Number.parseFloat(formData.paid_amount) || totals.total, totals.total)
      : 0;
    const mlBlocked = Boolean(mlCreationBlock.blocked);
    const attachMl = Boolean(formData.existing_ml_sheet_id) && mlBlockLoaded && !mlBlocked;
    setLoading(true);
    try {
      const payload = {
        pickup_booking: true,
        sender_location_hub_id: formData.sender_location_hub_id,
        receiver_location_hub_id: formData.receiver_location_hub_id,
        sender_contact: formData.sender_contact,
        receiver_contact: formData.receiver_contact,
        no_of_articles: formData.no_of_articles,
        amount: extras.freight,
        freight_charges: extras.freight,
        docket_fee: extras.docket,
        handling_charges: extras.handling,
        door_pickup_charges: extras.doorPickup,
        delivery_charges: extras.doorDelivery,
        loading_charges: extras.loading,
        unloading_charges: extras.unloading,
        toll_charges: extras.toll,
        fuel_surcharge: extras.fuel,
        packing_charges: extras.packing,
        insurance_charges: extras.insurance,
        sgst_percentage: gstRates.sgst,
        cgst_percentage: gstRates.cgst,
        igst_percentage: gstRates.igst,
        mode_of_payment: formData.mode_of_payment,
        paid_amount: paidAmount,
        payment_mode: formData.mode_of_payment === 'paid' ? formData.payment_mode : null,
        auto_ml_enabled: attachMl,
        auto_ml_mode: 'existing',
        existing_ml_sheet_id: formData.existing_ml_sheet_id || '',
        auto_ml_agent_user_id: user?.user_id || '',
        shipment_media: mediaFiles,
      };
      const response = await shipmentService.createShipment(payload);
      if (response.data?.requires_online_payment && response.data?.payment_intent) {
        setPaymentIntent(response.data.payment_intent);
        setShowPaymentQr(true);
        setLoading(false);
        return;
      }
      finishBookingSuccess(response);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to book shipment');
    } finally {
      setLoading(false);
    }
  };

  const handleOnlinePaymentSuccess = (intent) => {
    setShowPaymentQr(false);
    setPaymentIntent(null);
    finishBookingSuccess(null, intent?.result || {});
  };

  const handleOnlinePaymentCancel = () => {
    setShowPaymentQr(false);
    setPaymentIntent(null);
    Alert.info('Online payment cancelled. Shipment was not created.');
  };

  const extraRows = [
    ['Docket', extras.docket],
    ['Handling', extras.handling],
    ['Door pickup', extras.doorPickup],
    ['Door delivery', extras.doorDelivery],
    ['Loading', extras.loading],
    ['Toll', extras.toll],
    ['Fuel', extras.fuel],
    ['Packing', extras.packing],
    ['Insurance', extras.insurance],
  ].filter(([, value]) => value > 0);

  const paidAmountValue = Number.parseFloat(formData.paid_amount) || 0;
  const balanceDue = Math.max(0, ceilRupee(totals.total - paidAmountValue));
  const showPaidSection = formData.mode_of_payment === 'paid';
  const fieldWrap = 'flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-3 py-1.5 overflow-visible';
  const desktopLabel = 'block text-xs sm:text-sm font-medium text-secondary mb-1.5';
  const desktopInput =
    'w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400 bg-white';
  const mlPlaceholder = !formData.receiver_location_hub_id
    ? 'Select destination first'
    : receiverMlSheets.length
      ? `Book only (no ML sheet) · ${receiverMlSheets.length} available`
      : 'No vehicle for this destination today';
  const mlSelect = hasAutoMlAccess(user) ? (
    <div className="min-w-0">
      {mlBlockLoaded && mlCreationBlock.blocked && (
        <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-2">
          {mlCreationBlock.reason}
        </p>
      )}
      <PickupMlSheetSelect
        value={mlBlockLoaded && !mlCreationBlock.blocked ? formData.existing_ml_sheet_id : ''}
        sheets={receiverMlSheets}
        onChange={handleChange}
        disabled={!mlBlockLoaded || Boolean(mlCreationBlock.blocked) || !formData.receiver_location_hub_id}
        placeholder={mlPlaceholder}
      />
    </div>
  ) : null;
  const paidSection = showPaidSection ? (
    <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col min-w-0">
          <label className="text-xs font-medium text-gray-600 mb-1">Payment Mode</label>
          <SelectField
            variant="form"
            name="payment_mode"
            value={formData.payment_mode}
            onChange={handleChange}
            required
            className="w-full"
          >
            <option value="">Select Mode *</option>
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
          </SelectField>
        </div>
        <div className="flex flex-col min-w-0">
          <label className="text-xs font-medium text-gray-600 mb-1">Paid Amount</label>
          <input
            type="text"
            inputMode="decimal"
            name="paid_amount"
            value={formData.paid_amount}
            onChange={handleChange}
            placeholder="0.00"
            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400 bg-white"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/90 p-2.5 rounded-xl">
          <p className="text-[11px] text-gray-600 font-semibold">Paid Amount</p>
          <p className="text-base font-bold text-blue-600">₹{formData.paid_amount || '0.00'}</p>
        </div>
        <div className="bg-white/90 p-2.5 rounded-xl">
          <p className="text-[11px] text-gray-600 font-semibold">Balance Due</p>
          <p className="text-base font-bold text-blue-600">₹{balanceDue}</p>
        </div>
      </div>
      {formData.payment_mode === 'upi' && upiQrWillOpen && (
        <p className="text-[11px] text-gray-600">
          UPI QR will open after submit so the customer can pay on this phone.
        </p>
      )}
    </div>
  ) : null;
  const chargesSection = Number(formData.amount) > 0 ? (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm">
      <div className="flex justify-between text-gray-600">
        <span>Freight</span>
        <span>₹{extras.freight.toFixed(2)}</span>
      </div>
      {extraRows.map(([label, value]) => (
        <div key={label} className="flex justify-between text-gray-600 mt-1">
          <span>{label}</span>
          <span>₹{value.toFixed(2)}</span>
        </div>
      ))}
      {totals.gst > 0 && (
        <div className="flex justify-between text-gray-600 mt-1">
          <span>GST</span>
          <span>₹{totals.gst}</span>
        </div>
      )}
      <div className="flex justify-between font-semibold text-green-700 mt-2 pt-2 border-t border-gray-200">
        <span>Total Amount</span>
        <span>₹{totals.total}</span>
      </div>
      {extras.splitMode && (
        <p className="text-[11px] text-gray-500 mt-1.5">
          Extra charges are % of freight. Total is calculated.
        </p>
      )}
    </div>
  ) : null;

  return (
    <DashboardLayout fullWidth={!isMobileLayout} fillViewport={!isMobileLayout}>
      {isMobileLayout ? (
        <div className="max-w-xl mx-auto pb-6">
          <div className="bg-blue-50 text-blue-800 rounded-t-xl px-4 py-3 flex items-center justify-between border border-blue-100">
            <button type="button" onClick={() => navigate('/shipments')} className="text-xl leading-none text-blue-700">
              ‹
            </button>
            <h1 className="text-base font-semibold tracking-wide">Add Shipment</h1>
            <Package className="w-5 h-5 text-blue-600" />
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-b-xl shadow-sm p-4 sm:p-5 space-y-3">
            <div className={fieldWrap}>
              <MapPin className="w-5 h-5 text-blue-600 shrink-0" />
              <PickupLocationSelect
                name="sender_location_hub_id"
                value={formData.sender_location_hub_id}
                hubs={pickupHubs}
                placeholder="Enter Pickup Location"
                title="Select pickup location"
                onChange={handleChange}
                excludeId={formData.receiver_location_hub_id}
              />
            </div>

            <div className={fieldWrap}>
              <MapPin className="w-5 h-5 text-blue-600 shrink-0" />
              <PickupLocationSelect
                name="receiver_location_hub_id"
                value={formData.receiver_location_hub_id}
                hubs={destinationHubs}
                placeholder="Enter Destination"
                title="Select destination"
                onChange={handleChange}
                excludeId={formData.sender_location_hub_id}
              />
            </div>

            <div className={fieldWrap}>
              <Phone className="w-5 h-5 text-blue-600 shrink-0" />
              <input
                type="tel"
                name="receiver_contact"
                value={formData.receiver_contact}
                onChange={handleChange}
                placeholder="Enter Consignee Mobile Number"
                className="w-full py-2 outline-none text-sm"
              />
            </div>

            <div className={fieldWrap}>
              <Phone className="w-5 h-5 text-blue-600 shrink-0" />
              <input
                type="tel"
                name="sender_contact"
                value={formData.sender_contact}
                onChange={handleChange}
                placeholder="Enter Consigner Mobile Number"
                className="w-full py-2 outline-none text-sm"
              />
            </div>

            <div className={fieldWrap}>
              <Package className="w-5 h-5 text-blue-600 shrink-0" />
              <input
                type="text"
                inputMode="numeric"
                name="no_of_articles"
                value={formData.no_of_articles}
                onChange={handleChange}
                placeholder="Enter Number of Parcels"
                className="w-full py-2 outline-none text-sm"
              />
            </div>

            <div className={fieldWrap}>
              <IndianRupee className="w-5 h-5 text-blue-600 shrink-0" />
              <input
                type="text"
                inputMode="decimal"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder={extras.splitMode ? 'Enter freight (article rate)' : 'Enter Amount'}
                className="w-full py-2 outline-none text-sm"
              />
            </div>
            {extras.splitMode && (
              <p className="text-[11px] text-gray-500 -mt-1 px-1">
                Extra charges use % of freight. Total is calculated below.
              </p>
            )}

            <div className={fieldWrap}>
              <CreditCard className="w-5 h-5 text-blue-600 shrink-0" />
              <SelectField
                variant="form"
                name="mode_of_payment"
                value={formData.mode_of_payment}
                onChange={handleChange}
                className="w-full border-0 focus:ring-0 px-0"
              >
                {paymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </SelectField>
            </div>

            {paidSection}
            {chargesSection}

            {mlSelect && (
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Select vehicle (today&apos;s ML)</p>
                {mlSelect}
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Camera className="w-4 h-4 text-blue-600" /> Image Capture
              </p>
              <ShipmentMediaUploader
                files={mediaFiles}
                onFilesChange={setMediaFiles}
                dropzone
              />
            </div>

            <Button type="submit" loading={loading} icon={Send} className="w-full uppercase tracking-wide">
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </form>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="min-w-0 flex flex-col flex-1 min-h-0 md:[&_input]:py-1.5 md:[&_select]:py-1.5"
        >
          <div className="shrink-0 mb-3 pb-2 border-b border-gray-200/80">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="flex items-center gap-2 text-base sm:text-xl font-bold text-gray-700 truncate">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-buttonhoverTxt shrink-0" />
                <span className="truncate">Add Shipment</span>
              </h1>
              <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3 shrink-0">
                <Button variant="secondary" type="button" icon={ArrowLeft} onClick={() => navigate('/shipments')}>
                  Back
                </Button>
                <Button variant="secondary" type="button" onClick={() => navigate('/shipments')}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" icon={Send} loading={loading} disabled={loading}>
                  {loading ? 'Submitting...' : 'Create Shipment'}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-2">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 min-w-0 space-y-4">
                <div className="pb-2 border-b-2 border-gray-200">
                  <h2 className="text-base font-semibold text-gray-700">Booking Details</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className={desktopLabel}>Origin</label>
                    <PickupLocationSelect
                      variant="form"
                      name="sender_location_hub_id"
                      value={formData.sender_location_hub_id}
                      hubs={pickupHubs}
                      placeholder="Select pickup location"
                      title="Select pickup location"
                      onChange={handleChange}
                      excludeId={formData.receiver_location_hub_id}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={desktopLabel}>Destination</label>
                    <PickupLocationSelect
                      variant="form"
                      name="receiver_location_hub_id"
                      value={formData.receiver_location_hub_id}
                      hubs={destinationHubs}
                      placeholder="Select destination"
                      title="Select destination"
                      onChange={handleChange}
                      excludeId={formData.sender_location_hub_id}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={desktopLabel}>Consignee Mobile</label>
                    <input
                      type="tel"
                      name="receiver_contact"
                      value={formData.receiver_contact}
                      onChange={handleChange}
                      placeholder="Enter consignee mobile"
                      className={desktopInput}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={desktopLabel}>Consigner Mobile</label>
                    <input
                      type="tel"
                      name="sender_contact"
                      value={formData.sender_contact}
                      onChange={handleChange}
                      placeholder="Enter consigner mobile"
                      className={desktopInput}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={desktopLabel}>Number of Parcels</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      name="no_of_articles"
                      value={formData.no_of_articles}
                      onChange={handleChange}
                      placeholder="Enter number of parcels"
                      className={desktopInput}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={desktopLabel}>{extras.splitMode ? 'Freight (Article Rate)' : 'Amount'}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder={extras.splitMode ? 'Enter freight amount' : 'Enter amount'}
                      className={desktopInput}
                    />
                    {extras.splitMode && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        Extra charges use % of freight. Total is calculated below.
                      </p>
                    )}
                  </div>
                  <div className="min-w-0 md:col-span-2">
                    <label className={desktopLabel}>Payment Mode</label>
                    <SelectField
                      variant="form"
                      name="mode_of_payment"
                      value={formData.mode_of_payment}
                      onChange={handleChange}
                      className="w-full"
                    >
                      {paymentModes.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
                {paidSection}
                {chargesSection}
              </div>

              <div className="space-y-3 sm:space-y-4 min-w-0">
                {mlSelect && (
                  <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 min-w-0">
                    <div className="pb-2 mb-3 border-b-2 border-gray-200">
                      <h2 className="text-base font-semibold text-gray-700">
                        Today&apos;s ML Sheets
                        {receiverMlSheets.length > 0 ? (
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({receiverMlSheets.length})
                          </span>
                        ) : null}
                      </h2>
                    </div>
                    <p className={desktopLabel}>Select vehicle (today&apos;s ML)</p>
                    {mlSelect}
                  </div>
                )}
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 min-w-0">
                  <div className="pb-2 mb-3 border-b-2 border-gray-200">
                    <h2 className="text-base font-semibold text-gray-700 flex items-center gap-2">
                      <Camera className="w-4 h-4 text-blue-600" /> Photos &amp; Videos
                    </h2>
                  </div>
                  <ShipmentMediaUploader
                    files={mediaFiles}
                    onFilesChange={setMediaFiles}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
      <PaymentQrModal
        open={showPaymentQr}
        intent={paymentIntent}
        title="Scan UPI QR to Create Shipment"
        onSuccess={handleOnlinePaymentSuccess}
        onCancel={handleOnlinePaymentCancel}
      />
    </DashboardLayout>
  );
};

export default PickupManAddShipment;
